import React, { FC, useCallback, useState, useMemo } from 'react'
import { TouchableOpacity } from 'react-native'
import { graphql, useFragment } from 'react-relay'
import { useLocation } from 'react-router-dom'

import {
  Element,
  Icon,
  Link,
  LinkText,
  PresentationMetadata,
  PresentationVideoPlayer,
  Stack,
  Text,
  UserPrimaryPhoto,
  useInModal,
} from '@components'
import { FormattedMarkdown } from '@newComponents'

import PresentationEvents from './PresentationEvents'
import PresentationGuilds from './PresentationGuilds'

import { PresentationScreenContent_presentation$key } from './__generated__/PresentationScreenContent_presentation.graphql'

export const PresentationScreenContent: FC<{
  presentation: PresentationScreenContent_presentation$key
}> = (props) => {
  const location = useLocation()
  const presentation = useFragment(
    graphql`
      fragment PresentationScreenContent_presentation on Presentation {
        canEdit
        description
        presenterFirstName
        presenterLastName
        prettyUrl
        title
        videoSourceUrl

        presenter {
          slugId
          firstName
          lastName

          ...UserPrimaryPhoto_user
          ...UserItem_user
        }
        ...PresentationMetadata_presentation
        ...PresentationVideoPlayer_presentation
        ...PresentationEvents_presentation
        ...PresentationGuilds_presentation
      }
    `,
    props.presentation
  )
  const [dropDownIsOpen, setDropDownIsOpen] = useState(false)
  const handlePress = useCallback(
    () => setDropDownIsOpen((value) => !value),
    []
  )
  const closeDropDown = useCallback(() => setDropDownIsOpen(false), [])

  const isInModal = useInModal()

  let presenterElement

  if (presentation.presenter) {
    presenterElement = (
      <Stack direction='horizontal' gap={1}>
        <Text size={4}>Presentation by</Text>
        <Link to={`/${presentation.presenter.slugId}`}>
          <LinkText size={4}>
            {presentation.presenter?.firstName}{' '}
            {presentation.presenter?.lastName}
          </LinkText>
        </Link>
      </Stack>
    )
  } else {
    presenterElement = (
      <Text size={4}>
        Presentation by {presentation.presenterFirstName}{' '}
        {presentation.presenterLastName}
      </Text>
    )
  }

  return (
    <Stack expand variant='wrap'>
      <PresentationMetadata presentation={presentation} />

      <PresentationVideoPlayer presentation={presentation} />

      <Element
        component='PresentationModalDetails'
        variant={isInModal ? 'inModal' : 'inScreen'}
      >
        <Stack gap={5} expand variant='wrap'>
          <Stack direction='horizontal' variant='dropDownContainer'>
            <Stack direction='horizontal' expand gap={3}>
              {presentation.presenter && (
                <Link to={`/${presentation.presenter.slugId}`}>
                  <UserPrimaryPhoto user={presentation.presenter} />
                </Link>
              )}

              <Stack gap={2} expand>
                <Text size={6} variant='heading'>
                  {presentation.title}
                </Text>

                {presenterElement}
              </Stack>
              {!!presentation.canEdit && (
                <Stack gap={2}>
                  <TouchableOpacity onPress={handlePress}>
                    <Icon
                      size={6}
                      name={dropDownIsOpen ? 'chevron-up' : 'chevron-down'}
                    />
                  </TouchableOpacity>
                  {dropDownIsOpen && (
                    <Stack>
                      <Element
                        component='NavigationDropDownMenu'
                        tokens={{ position: 'absolute' }}
                        variant='PresentationDropDownMenu'
                      >
                        {!!presentation.canEdit && (
                          <Link
                            beforeNavigate={closeDropDown}
                            options={{
                              state: {
                                backgroundLocation:
                                  location.state?.backgroundLocation ||
                                  location,
                              },
                            }}
                            to={`/presentations/${presentation.prettyUrl}/edit`}
                          >
                            <Stack direction='horizontal' gap={4}>
                              <Icon name='edit' variant='dropDownOption' />
                              <Text>Edit</Text>
                            </Stack>
                          </Link>
                        )}
                      </Element>
                    </Stack>
                  )}
                </Stack>
              )}
            </Stack>
          </Stack>

          {!!presentation.description && (
            <FormattedMarkdown textSize='$5'>
              {presentation.description}
            </FormattedMarkdown>
          )}

          <PresentationEvents presentation={presentation} />
          <PresentationGuilds presentation={presentation} />
        </Stack>
      </Element>
    </Stack>
  )
}

export default PresentationScreenContent
